import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Award,
  Users,
  Leaf,
  Clock,
  ShieldCheck,
  Star,
  ArrowRight,
  CheckCircle2,
  Heart
} from "lucide-react";

const TORONTO_IMAGE = "https://images.unsplash.com/photo-1507669370444-19f0836d34cf?crop=entropy&cs=srgb&fm=jpg&w=1200";

const values = [
  {
    icon: Star,
    title: "Excellence",
    description: "We strive for perfection in every clean, paying attention to the smallest details that make the biggest difference."
  },
  {
    icon: ShieldCheck,
    title: "Trust",
    description: "Our fully vetted, insured team treats your property with the same care and respect as our own."
  },
  {
    icon: Leaf,
    title: "Sustainability",
    description: "We use eco-friendly products that are safe for your family, pets, and the environment."
  },
  {
    icon: Heart,
    title: "Care",
    description: "We genuinely care about our clients' satisfaction and build lasting relationships based on trust."
  }
];

const stats = [
  { value: "5+", label: "Years of Experience" },
  { value: "2000+", label: "Happy Clients" },
  { value: "15000+", label: "Cleanings Completed" },
  { value: "100%", label: "Satisfaction Rate" }
];

const team = [
  {
    name: "Quality Assurance",
    role: "Inspection Team",
    description: "Every cleaning is followed by a quality check to ensure our high standards are met."
  },
  {
    name: "Customer Support",
    role: "Available 7 Days",
    description: "Our friendly support team is here to help with scheduling, questions, and special requests."
  },
  {
    name: "Cleaning Specialists",
    role: "Trained Professionals",
    description: "Our cleaners undergo extensive training in the latest cleaning techniques and safety protocols."
  }
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="bg-[#FAFAF9]">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-[#064E3B] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src={TORONTO_IMAGE} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-widest font-semibold text-[#C0A062] mb-4">
              About Us
            </p>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-[1.1] text-white mb-6">
              Toronto's Trusted Premium Cleaning Service
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-white/80">
              Founded with a vision to transform the cleaning industry, Toronto Premium Cleaning delivers exceptional service that exceeds expectations every time.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest font-semibold text-[#064E3B] mb-4">
                Our Story
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0C0A09] mb-6">
                A Passion for Pristine Spaces
              </h2>
              <div className="space-y-4 text-[#78716C] leading-relaxed">
                <p>
                  Toronto Premium Cleaning was born from a simple belief: everyone deserves a clean, healthy environment to live and work in. What started as a small residential cleaning service has grown into one of the GTA's most trusted cleaning companies.
                </p>
                <p>
                  Our journey began when we noticed a gap in the market for truly premium cleaning services that combine meticulous attention to detail with outstanding customer service. We set out to create a company that would redefine what clients expect from their cleaning service.
                </p>
                <p>
                  Today, we're proud to serve thousands of satisfied clients across the Greater Toronto Area, from busy families and professionals to property managers and business owners. Our commitment to excellence remains as strong as ever.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={TORONTO_IMAGE}
                  alt="Toronto Skyline"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#C0A062] text-white p-6 rounded-xl shadow-xl">
                <p className="text-4xl font-bold">5+</p>
                <p className="text-sm">Years Serving GTA</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest font-semibold text-[#064E3B] mb-4">
              Our Values
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0C0A09] mb-4">
              What Drives Us Every Day
            </h2>
            <div className="section-divider" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={value.title}
                className="text-center p-8 rounded-xl bg-[#FAFAF9] hover:bg-[#F0FDF4] transition-colors duration-300"
                data-testid={`value-${index}`}
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#064E3B] flex items-center justify-center">
                  <value.icon className="w-8 h-8 text-[#C0A062]" />
                </div>
                <h3 className="text-xl font-semibold text-[#0C0A09] mb-3">
                  {value.title}
                </h3>
                <p className="text-[#78716C] text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-[#064E3B]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <p className="text-4xl md:text-5xl font-bold text-[#C0A062] mb-2">
                  {stat.value}
                </p>
                <p className="text-white/80 text-sm md:text-base">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-32 bg-[#FAFAF9]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest font-semibold text-[#064E3B] mb-4">
                Why Choose Us
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0C0A09] mb-8">
                The Toronto Premium Difference
              </h2>
              <ul className="space-y-6">
                {[
                  "Fully insured and bonded cleaning professionals",
                  "Eco-friendly, hospital-grade cleaning products",
                  "Customized cleaning plans for your specific needs",
                  "100% satisfaction guarantee on every service",
                  "Flexible scheduling including weekends",
                  "Consistent quality with the same trusted cleaners",
                  "Transparent pricing with no hidden fees",
                  "Easy online booking and communication"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 text-[#064E3B] mt-0.5 flex-shrink-0" />
                    <span className="text-[#78716C]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-1 gap-6">
              {team.map((member, index) => (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-[#C0A062]"
                  data-testid={`team-${index}`}
                >
                  <h3 className="text-lg font-semibold text-[#0C0A09] mb-1">
                    {member.name}
                  </h3>
                  <p className="text-[#064E3B] text-sm font-medium mb-2">{member.role}</p>
                  <p className="text-[#78716C] text-sm">{member.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#F0FDF4]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0C0A09] mb-6">
            Ready to Experience the Difference?
          </h2>
          <p className="text-lg text-[#78716C] mb-10 max-w-2xl mx-auto">
            Join thousands of satisfied clients across the GTA who trust Toronto Premium Cleaning for their homes and businesses.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking" data-testid="about-book-btn">
              <Button className="bg-[#064E3B] hover:bg-[#065F46] text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg shadow-emerald-900/20">
                Book Your Cleaning
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact" data-testid="about-contact-btn">
              <Button className="bg-transparent border-2 border-[#064E3B] text-[#064E3B] hover:bg-[#064E3B] hover:text-white rounded-full px-8 py-6 text-lg font-medium">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
